/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "nesting" }] */
import { FormEvent, useState } from "react";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";
import { useScenarioAPI } from "./ScenarioAPI";
import { ScenarioGuide, metricLabels } from "./ScenarioGuide";
import { FULL_FILL_POLICY } from "./ActivationPanel";
import { ScenarioOperation, ScenarioView, scenarioID } from "./ScenarioTypes";

const field = (form: FormData, name: string) =>
  String(form.get(name) || "").trim();
const initialSelection = () =>
  new URLSearchParams(window.location.search).get("scenario") || "";

export function ScenarioTab() {
  const [selected, setSelected] = useState(initialSelection);
  const [before, setBefore] = useState<ScenarioView>();
  const [localError, setLocalError] = useState<string>();
  const api = useScenarioAPI(selected);
  const { view } = api;
  const disabled =
    !api.verified ||
    api.busy ||
    Boolean(api.retryCommand || api.pendingCommand);
  const select = (id: string) => {
    setSelected(id);
    setBefore(undefined);
    const url = new URL(window.location.href);
    url.searchParams.set("scenario", id);
    window.history.replaceState(null, "", url.toString());
  };
  const run = async (
    operation: ScenarioOperation,
    payload: Record<string, unknown>,
    requestID?: string
  ) => {
    setLocalError(undefined);
    setBefore(view);
    try {
      const id = operation === "create" ? scenarioID() : selected;
      await api.command(id, operation, payload, requestID);
      if (operation === "create") select(id);
    } catch (error) {
      setLocalError(affiliateError(error));
    }
  };
  const submit =
    (
      operation: ScenarioOperation,
      payload: (form: FormData) => Record<string, unknown>
    ) =>
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      try {
        run(operation, payload(new FormData(event.currentTarget)));
      } catch (error) {
        setLocalError(affiliateError(error));
      }
    };
  return (
    <div>
      <section>
        <h2>Shared affiliate scenario lab</h2>
        <p>
          <strong>
            Simulation only — no real funds, wallet connection or gas.
          </strong>{" "}
          This runs the backend affiliate calculations with fictional
          instruments and an isolated clock. Advancing it does not change the
          exchange clock or place real trades.
        </p>
        <p>
          Google admin sign-in and system read/write permissions are required.
          The guided demo checks program economics; real order execution and
          payment delivery remain separate testnet checks.
        </p>
        <form
          onSubmit={submit("create", (form) => ({
            name: field(form, "name"),
            tier: "VIP3",
            waiver_policy: FULL_FILL_POLICY,
          }))}
        >
          <fieldset disabled={disabled}>
            <label>
              Scenario name
              <input
                name="name"
                defaultValue="Tim’s affiliate walkthrough"
                maxLength={120}
                required
              />
            </label>
            <button type="submit">Create guided VIP3 scenario</button>
          </fieldset>
        </form>
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 16,
            alignItems: "end",
          }}
        >
          <label>
            Open shared scenario
            <select
              value={selected}
              disabled={
                api.busy || Boolean(api.pendingCommand || api.retryCommand)
              }
              onChange={(event) => select(event.target.value)}
            >
              <option value="">Choose a scenario</option>
              {api.list.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} · {item.trades} trades · run {item.generation}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={api.busy}
            onClick={() =>
              api
                .refresh()
                .catch((error) => setLocalError(affiliateError(error)))
            }
          >
            Refresh scenario
          </button>
          <button
            type="button"
            disabled={!view || disabled}
            onClick={() => {
              if (!view || disabled) return;
              const url = URL.createObjectURL(
                new Blob([JSON.stringify(view, null, 2)], {
                  type: "application/json",
                })
              );
              const link = document.createElement("a");
              link.href = url;
              link.download = `affiliate-scenario-${encodeURIComponent(
                view.id
              )}-generation-${view.generation}.json`;
              document.body.appendChild(link);
              link.click();
              link.remove();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}
          >
            Download scenario state
          </button>
          {selected && (
            <a href={`?scenario=${encodeURIComponent(selected)}`}>
              Share this scenario
            </a>
          )}
        </div>
        {selected && (
          <p>
            <code>{selected}</code>
          </p>
        )}
      </section>
      {(localError || api.error) && (
        <p role="alert">{localError || api.error}</p>
      )}
      {api.retryCommand && (
        <div role="alert">
          The result is unconfirmed. Retry the exact same request to avoid
          applying it twice.{" "}
          <button
            type="button"
            disabled={api.busy}
            onClick={() => {
              setLocalError(undefined);
              api
                .retry()
                ?.then((id) => {
                  if (api.retryCommand?.operation === "create") select(id);
                })
                .catch((error) => setLocalError(affiliateError(error)));
            }}
          >
            Retry unconfirmed scenario request
          </button>
        </div>
      )}
      {api.pendingCommand && (
        <p role="status">
          Request accepted; waiting for the backend to apply it. Controls unlock
          when this request appears in scenario state.{" "}
          <code>{api.pendingCommand.request_id}</code>
        </p>
      )}
      {api.receipt && !api.pendingCommand && (
        <p role="status">
          Backend applied request <code>{api.receipt}</code>.
        </p>
      )}
      {view && (
        <>
          <section>
            <h2>
              {view.name} · run {view.generation}
            </h2>
            <p>
              <strong>Scenario clock: {view.clock}</strong>
            </p>
            <p>
              Fixture affiliate: <code>{view.fixtures.affiliate}</code>
              <br />
              Fixture trader: <code>{view.fixtures.trader}</code>
              <br />
              Referral code: <code>{view.fixtures.code}</code>
            </p>
          </section>
          <ScenarioGuide view={view} disabled={disabled} run={run} />
          {before && before.id === view.id && (
            <section>
              <h2>Before and after the last action</h2>
              <table>
                <thead>
                  <tr>
                    <th>Measure</th>
                    <th>Before</th>
                    <th>Latest backend value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(metricLabels).map(([key, label]) => (
                    <tr key={key}>
                      <th>{label}</th>
                      <td>{before.summary[key]}</td>
                      <td>{view.summary[key]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          <details>
            <summary>
              Advanced scenario controls — custom trades, instruments and
              program rules
            </summary>
            <p>
              These change the shared scenario and can take it outside the
              guided expectations. All amounts remain fictional.
            </p>
            <section>
              <h2>Move the isolated clock</h2>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => run("advance_clock", { seconds: 86400 })}
                >
                  Advance 1 day
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => run("advance_clock", { seconds: 604800 })}
                >
                  Advance 7 days
                </button>
              </div>
              <form
                onSubmit={submit("advance_clock", (form) => ({
                  seconds: Number(field(form, "days")) * 86400,
                }))}
              >
                <fieldset disabled={disabled}>
                  <label>
                    Custom days
                    <input
                      name="days"
                      type="number"
                      min="1"
                      max="3650"
                      step="1"
                      defaultValue="1"
                      required
                    />
                  </label>
                  <button type="submit">Advance custom days</button>
                </fieldset>
              </form>
            </section>
            <section>
              <h2>Simulate a trade</h2>
              <form
                onSubmit={submit("trade", (form) => ({
                  wallet: field(form, "wallet"),
                  instrument_id: field(form, "instrument"),
                  notional: field(form, "notional"),
                  premium: field(form, "premium"),
                  fills: Number(field(form, "fills")),
                  side: field(form, "side"),
                  staking_discount: field(form, "discount"),
                }))}
              >
                <fieldset disabled={disabled}>
                  <label>
                    Fixture trader
                    <select name="wallet" defaultValue={view.fixtures.trader}>
                      {view.fixtures.traders.map((wallet) => (
                        <option key={wallet} value={wallet}>
                          {wallet}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Fictional instrument
                    <select name="instrument" defaultValue="BTC-PERP">
                      {view.instruments.map((instrument) => (
                        <option key={instrument.id} value={instrument.id}>
                          {instrument.name} ({instrument.id})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Total notional ($)
                    <input
                      name="notional"
                      defaultValue="1000000"
                      pattern="[0-9]+(\.[0-9]{1,6})?"
                      required
                    />
                  </label>
                  <label>
                    Total options premium ($)
                    <input
                      name="premium"
                      defaultValue="0"
                      pattern="[0-9]+(\.[0-9]{1,6})?"
                      required
                    />
                  </label>
                  <label>
                    Number of fills
                    <input
                      name="fills"
                      type="number"
                      min="1"
                      max="100"
                      step="1"
                      defaultValue="1"
                      required
                    />
                  </label>
                  <label>
                    Trade side
                    <select name="side">
                      <option value="taker">Taker</option>
                      <option value="maker">Maker</option>
                    </select>
                  </label>
                  <label>
                    Staking discount fraction (0–1)
                    <input
                      name="discount"
                      defaultValue="0.2"
                      pattern="(0(\.[0-9]{1,6})?|1(\.0{1,6})?)"
                      required
                    />
                  </label>
                  <button type="submit">Simulate trade</button>
                </fieldset>
              </form>
              <p>
                Notional and premium are totals split across the requested
                fills. A 0.2 staking discount means 20%. Use a positive premium
                for options. The other fixture traders must first be attached to
                a code.
              </p>
            </section>
            <section>
              <h2>Create a fictional instrument</h2>
              <form
                onSubmit={submit("define_instrument", (form) => ({
                  id: field(form, "id"),
                  name: field(form, "name"),
                  product: field(form, "product"),
                  ...(field(form, "expiry")
                    ? { expiry: new Date(field(form, "expiry")).toISOString() }
                    : {}),
                  ...(field(form, "strike")
                    ? {
                        strike: field(form, "strike"),
                        option_type: field(form, "option_type"),
                      }
                    : {}),
                }))}
              >
                <fieldset disabled={disabled}>
                  <label>
                    Instrument ID
                    <input
                      name="id"
                      defaultValue="DEMO-BTC-CALL"
                      pattern="[A-Za-z0-9][A-Za-z0-9_.-]{0,63}"
                      required
                    />
                  </label>
                  <label>
                    Instrument name
                    <input
                      name="name"
                      defaultValue="Fictional BTC call"
                      required
                    />
                  </label>
                  <label>
                    Product
                    <select name="product" defaultValue="option">
                      <option value="perpetual">Perpetual</option>
                      <option value="option">Option</option>
                    </select>
                  </label>
                  <label>
                    Option expiry (local time)
                    <input name="expiry" type="datetime-local" />
                  </label>
                  <label>
                    Option strike
                    <input name="strike" defaultValue="100000" />
                  </label>
                  <label>
                    Option type
                    <select name="option_type">
                      <option value="call">Call</option>
                      <option value="put">Put</option>
                    </select>
                  </label>
                  <button type="submit">Define fictional instrument</button>
                </fieldset>
              </form>
              <p>
                This creates an instrument description inside the scenario, not
                a deployed contract or exchange market.
              </p>
            </section>
            <section>
              <h2>Apply a program command</h2>
              <form
                onSubmit={submit("program", (form) => ({
                  operation: field(form, "operation"),
                  actor: field(form, "actor"),
                  payload: JSON.parse(field(form, "payload")),
                }))}
              >
                <fieldset disabled={disabled}>
                  <label>
                    Fixture acting as caller
                    <select name="actor" defaultValue={view.fixtures.affiliate}>
                      <option value={view.fixtures.affiliate}>Affiliate</option>
                      {view.fixtures.traders.map((wallet, index) => (
                        <option key={wallet} value={wallet}>
                          Trader {index + 1} — {wallet}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Program operation
                    <select name="operation" defaultValue="grant_welcome">
                      {[
                        "grant_welcome",
                        "allocate_allowance",
                        "set_rates",
                        "set_code_rates",
                        "approve",
                        "upgrade",
                        "downgrade",
                        "terminate",
                        "create_code",
                        "attach",
                      ].map((operation) => (
                        <option key={operation} value={operation}>
                          {operation}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Program payload (JSON)
                    <textarea
                      name="payload"
                      key={view.id}
                      defaultValue={JSON.stringify(
                        { affiliate: view.fixtures.affiliate, points: "640" },
                        null,
                        2
                      )}
                      required
                    />
                  </label>
                  <button type="submit">Apply scenario program command</button>
                </fieldset>
              </form>
              <p>
                The backend validates every command against the scenario
                fixtures. Example allowance:{" "}
                <code>
                  {JSON.stringify({
                    wallet: view.fixtures.trader,
                    dollars: "160",
                  })}
                </code>
                .
              </p>
            </section>
            <section>
              <h2>Simulated payouts and reset</h2>
              <button
                type="button"
                disabled={disabled}
                onClick={() => run("simulate_payout", {})}
              >
                Simulate eligible cash payout
              </button>
              <p>
                Only the scenario ledger changes. DAO balances remain unpaid.
                Reset starts a fresh VIP3 run in this shared scenario; it
                replaces the current fictional balances and history.
              </p>
              <button
                type="button"
                disabled={disabled}
                onClick={() =>
                  run("reset", {
                    name: view.name,
                    tier: "VIP3",
                    waiver_policy: FULL_FILL_POLICY,
                  })
                }
              >
                Reset this simulation
              </button>
            </section>
          </details>
          <section>
            <h2>Fictional trade history</h2>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Scenario time / instrument</th>
                    <th>Notional / fills</th>
                    <th>Paid / waived ($)</th>
                    <th>Cash / DAO ($)</th>
                    <th>Points earned</th>
                  </tr>
                </thead>
                <tbody>
                  {view.trades.map((trade) => (
                    <tr key={trade.id}>
                      <td>
                        {trade.at}
                        <br />
                        {trade.request.instrument_id}
                      </td>
                      <td>
                        {trade.request.notional} / {trade.request.fills}
                      </td>
                      <td>
                        {trade.paid_fee} / {trade.waived_fee}
                      </td>
                      <td>
                        {trade.cash_usd} / {trade.dao_usd}
                      </td>
                      <td>{trade.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!view.trades.length && <p>No simulated trades yet.</p>}
          </section>
          <section>
            <h2>Provisional rules used by this scenario</h2>
            <ul>
              {view.dashboard.decisions
                .filter((decision) => decision.provisional)
                .map((decision) => (
                  <li key={decision.key}>
                    <code>{decision.key}</code>: {decision.value}
                  </li>
                ))}
            </ul>
          </section>
          <details>
            <summary>Inspect scenario backend state</summary>
            <pre>{JSON.stringify(view, null, 2)}</pre>
          </details>
        </>
      )}
    </div>
  );
}
